import { TooltipButton } from '@/components/Button';
import Text from '@/components/Text';
import {
    faArrowDownLeftAndArrowUpRightToCenter,
    faArrowUpRightAndArrowDownLeftFromCenter,
    faTimes
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';
import { useDatasetContext } from '../../contexts/DatasetContext';
import { EditorSteps } from '../constants';
import { useTileEditorStepsContext } from '../contexts/TileEditorStepsContext';
import { useTileEditorStore } from '../state/TileEditorStoreProvider';

interface StepTitleAndControlsProps {
    title: string;
    description?: ReactNode;
    helpLink?: string;
}

export const StepTitleAndControls: React.FC<StepTitleAndControlsProps> = ({ title, description, helpLink }) => {
    const { dispatch } = useTileEditorStore((s) => ({ dispatch: s.dispatch }));
    const { canDock } = useTileEditorStepsContext();
    const { config } = useDatasetContext();

    const { currentEditorStep, isDocked, setIsDocked, setCurrentEditorStep } = useTileEditorStepsContext();

    return (
        <div className='flex items-start'>
            <div className='flex-1 space-y-1'>
                <Text.H3 className='text-textPrimary' data-testid='stepTitle'>
                    {title}
                </Text.H3>

                {description && <Text.Body className='text-textSecondary'>{description}</Text.Body>}
            </div>

            {canDock && (
                <div className='inline-flex pl-4 ml-auto space-x-3 shrink-0'>
                    {helpLink && (
                        <a
                            href={helpLink}
                            className='mr-2 text-textLink hover:underline focus:underline'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            Learn more
                        </a>
                    )}
                    <TooltipButton
                        title={isDocked ? 'Undock' : 'Dock'}
                        variant='tertiary'
                        onClick={() => setIsDocked?.(!isDocked)}
                        tabIndex={999}
                        data-testid='dockToggle'
                    >
                        <FontAwesomeIcon
                            icon={
                                isDocked
                                    ? faArrowDownLeftAndArrowUpRightToCenter
                                    : faArrowUpRightAndArrowDownLeftFromCenter
                            }
                        />
                    </TooltipButton>

                    <TooltipButton
                        title={isDocked ? 'Undock and close' : 'Close'}
                        variant='tertiary'
                        onClick={() => {
                            if (currentEditorStep === EditorSteps.dataStream && !config.dataStream) {
                                // Clear filters when closing the data stream step
                                dispatch({
                                    type: 'dataStream.updateFilters',
                                    filters: {
                                        dataSourceId: undefined,
                                        objectType: undefined,
                                        sourceType: undefined,
                                        scopeId: undefined,
                                        tag: undefined,
                                        search: ''
                                    }
                                });
                            }

                            setCurrentEditorStep(undefined);
                            setIsDocked?.(false);
                        }}
                        tabIndex={999}
                        data-testid='closeStep'
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </TooltipButton>
                </div>
            )}
        </div>
    );
};
